import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Plus, FileText, Trash2, Eye, Edit2, Search, FileArchive } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPI_DOCUMENTO = {
  CDI: 'Contratto di Lavoro (CDI)',
  TESSERA_ELETTORALE: 'Tessera Elettorale',
  ALTRO: 'Altro Documento'
};

const STATI_DOCUMENTO = {
  ATTIVO: { label: 'Attivo', color: 'bg-green-100 text-green-800' },
  SCADUTO: { label: 'Scaduto', color: 'bg-yellow-100 text-yellow-800' },
  REVOCATO: { label: 'Revocato', color: 'bg-red-100 text-red-800' }
};

const Documenti = () => {
  const { canManageDocumenti, isSindacatoAdmin } = useAuth();
  const [documenti, setDocumenti] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [filterTipo, setFilterTipo] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  const [formData, setFormData] = useState({
    tipo: 'CDI',
    titolo: '',
    contenuto: '',
    utente_id: '',
    note: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [docRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/documenti`),
        isSindacatoAdmin() ? axios.get(`${API_URL}/users`) : Promise.resolve({ data: [] })
      ]);
      setDocumenti(docRes.data);
      setUtenti(usersRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/documenti`, formData);
      toast.success('Documento creato');
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo documento?')) return;
    try {
      await axios.delete(`${API_URL}/documenti/${docId}`);
      toast.success('Documento eliminato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'eliminazione');
    }
  };

  const handleUpdateStato = async (docId, nuovoStato) => {
    try {
      await axios.patch(`${API_URL}/documenti/${docId}`, { stato: nuovoStato });
      toast.success('Stato aggiornato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nell\'aggiornamento');
    }
  };

  const resetForm = () => {
    setFormData({
      tipo: 'CDI',
      titolo: '',
      contenuto: '',
      utente_id: '',
      note: ''
    });
  };

  const viewDocument = (doc) => {
    setSelectedDoc(doc);
    setViewDialogOpen(true);
  };

  const filteredDocumenti = documenti.filter(doc => {
    const matchesTipo = filterTipo === 'all' || doc.tipo === filterTipo;
    const matchesSearch = searchQuery === '' || 
      doc.titolo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.utente_nome.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTipo && matchesSearch;
  });

  return (
    <div className="space-y-6" data-testid="documenti-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-slate-900">Documenti</h1>
          <p className="text-slate-600 mt-1">Gestione CDI e Tessere Elettorali</p>
        </div>
        
        {canManageDocumenti() && (
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button data-testid="create-documento-btn" className="gap-2">
                <Plus className="w-4 h-4" />
                Nuovo Documento
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Crea Nuovo Documento</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="tipo">Tipo Documento</Label>
                    <Select
                      value={formData.tipo}
                      onValueChange={(value) => setFormData({...formData, tipo: value})}
                    >
                      <SelectTrigger data-testid="documento-tipo-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(TIPI_DOCUMENTO).map(([key, label]) => (
                          <SelectItem key={key} value={key}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="utente">Destinatario</Label>
                    <Select
                      value={formData.utente_id}
                      onValueChange={(value) => setFormData({...formData, utente_id: value})}
                    >
                      <SelectTrigger data-testid="documento-utente-select">
                        <SelectValue placeholder="Seleziona utente" />
                      </SelectTrigger>
                      <SelectContent>
                        {utenti.map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.nome} {u.cognome} ({u.username})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="titolo">Titolo</Label>
                  <Input
                    id="titolo"
                    data-testid="documento-titolo-input"
                    value={formData.titolo}
                    onChange={(e) => setFormData({...formData, titolo: e.target.value})}
                    placeholder="Titolo del documento"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="contenuto">Contenuto</Label>
                  <Textarea
                    id="contenuto"
                    data-testid="documento-contenuto-input"
                    value={formData.contenuto}
                    onChange={(e) => setFormData({...formData, contenuto: e.target.value})}
                    placeholder="Contenuto del documento..."
                    rows={6}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="note">Note (opzionale)</Label>
                  <Input
                    id="note"
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    placeholder="Note aggiuntive..."
                  />
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Annulla
                  </Button>
                  <Button type="submit" data-testid="save-documento-btn">
                    Crea Documento
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Filtri */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Cerca per titolo o destinatario..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={filterTipo} onValueChange={setFilterTipo}>
          <SelectTrigger className="w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            {Object.entries(TIPI_DOCUMENTO).map(([key, label]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Dialog visualizzazione */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedDoc?.titolo}</DialogTitle>
          </DialogHeader>
          {selectedDoc && (
            <div className="space-y-4">
              <div className="flex gap-4">
                <Badge>{TIPI_DOCUMENTO[selectedDoc.tipo]}</Badge>
                <Badge className={STATI_DOCUMENTO[selectedDoc.stato]?.color}>
                  {STATI_DOCUMENTO[selectedDoc.stato]?.label}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-slate-500">Destinatario</p>
                <p className="font-medium">{selectedDoc.utente_nome}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Contenuto</p>
                <div className="p-4 bg-slate-50 rounded-lg whitespace-pre-wrap">
                  {selectedDoc.contenuto}
                </div>
              </div>
              {selectedDoc.note && (
                <div>
                  <p className="text-sm text-slate-500">Note</p>
                  <p>{selectedDoc.note}</p>
                </div>
              )}
              <div className="flex justify-between text-sm text-slate-500">
                <span>Creato da: {selectedDoc.created_by_nome}</span>
                <span>{new Date(selectedDoc.created_at).toLocaleDateString('it-IT')}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lista documenti */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
        </div>
      ) : filteredDocumenti.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileArchive className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">Nessun documento trovato</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredDocumenti.map((doc) => (
            <Card key={doc.id} data-testid={`documento-card-${doc.id}`}>
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-400" />
                    <CardTitle className="text-base">{doc.titolo}</CardTitle>
                  </div>
                  <Badge className={STATI_DOCUMENTO[doc.stato]?.color}>
                    {STATI_DOCUMENTO[doc.stato]?.label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Tipo:</span>
                    <span>{TIPI_DOCUMENTO[doc.tipo]}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Destinatario:</span>
                    <span className="font-medium">{doc.utente_nome}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Data:</span>
                    <span>{new Date(doc.created_at).toLocaleDateString('it-IT')}</span>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => viewDocument(doc)}
                    className="flex-1"
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    Visualizza
                  </Button>
                  {canManageDocumenti() && (
                    <>
                      <Select
                        value={doc.stato}
                        onValueChange={(value) => handleUpdateStato(doc.id, value)}
                      >
                        <SelectTrigger className="w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATI_DOCUMENTO).map(([key, val]) => (
                            <SelectItem key={key} value={key}>{val.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(doc.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Documenti;
